import React from 'react';
import { SaveButton } from '@shared';
import type { SaveButtonProps } from '@shared';

/**
 * Admin wrapper around the shared `SaveButton`:
 * Spanish labels, errors surfaced as a toast, no router-aware redirect (the
 * admin never creates-then-navigates from a save button). Admin is
 * Spanish-only with no `useLocale`.
 *
 * Contract (from the shared component): `onSave` resolves → green «Guardado»
 * pulse; resolves `false` → stays quiet (validation the handler already
 * surfaced); throws → the error toast. Pass `dirty` from `useDirtyTracker` so
 * the button is grey until something changes.
 *
 * `WriteGuardProvider` is not mounted in the admin, so `requires` is a no-op
 * here (kept for parity with the tenant).
 */
export function adminFormatError(err: unknown): string {
    return err instanceof Error && err.message ? err.message : 'Error al guardar';
}

type AdminSaveButtonProps = Omit<SaveButtonProps, 'saveLabel' | 'savedLabel' | 'formatError'> & {
    saveLabel?: string;
    savedLabel?: string;
    formatError?: (err: unknown) => string;
};

const AdminSaveButton: React.FC<AdminSaveButtonProps> = ({
    saveLabel = 'Guardar',
    savedLabel = 'Guardado',
    formatError = adminFormatError,
    responsiveLabel = false,
    ...props
}) => (
    <SaveButton
        {...props}
        saveLabel={saveLabel}
        savedLabel={savedLabel}
        formatError={formatError}
        responsiveLabel={responsiveLabel}
    />
);

export default AdminSaveButton;
